import styled from '@emotion/styled';
import { useState, useMemo, memo } from 'react';
import { ThumbsUp, ThumbsDown, Link as LinkIcon, ShareNetwork as ShareIcon, Heart } from 'phosphor-react';
import { theme } from '../../styles/theme';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StudioCard, Studio } from './StudioCard';
import { LiveEvents } from './LiveEvents';
import { BreathingExerciseCard, BreathingExercise } from './BreathingExerciseCard';
import { ContentRecommendations } from './ContentRecommendations';

export interface Message {
  id: number | string;
  dbId?: number | string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: Date;
  isStreaming?: boolean;
  imageUrl?: string;
  agent?: string;
  recommendations?: any[];
  conversationSummary?: string;
  crisisAlert?: boolean;
  // Faded footnote under the crisis alert explaining it was sent automatically.
  crisisNotice?: string;
  // True for client-side stock messages (e.g. canned welcome greetings) that
  // should not show like/dislike rating actions — there's nothing to rate.
  hideActions?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  // Receive the message id so parents can pass ONE stable callback for the
  // whole list — per-message inline closures would defeat React.memo below.
  onLike?: (messageId: string | number) => void;
  onDislike?: (messageId: string | number) => void;
  showActions?: boolean;
  onStudioClick?: (studioId: string) => void;
  onEventClick?: (eventId: string) => void;
  onInternalLinkClick?: (url: string) => boolean;
  onSaveMealPlan?: (content: string, title: string) => void;
  onRedirectToAgent?: (agentId: string) => void;
}

const BubbleContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  margin-bottom: ${theme.spacing.lg};
  animation: slideIn 0.2s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Bubble = styled.div<{ $isUser: boolean }>`
  max-width: 85%;
  padding: ${({ $isUser }) =>
    $isUser ? `${theme.spacing.md} ${theme.spacing.lg}` : `${theme.spacing.md} 0`};
  border-radius: ${theme.borderRadius.message};
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  line-height: ${theme.typography.lineHeight.normal};
  word-wrap: break-word;
  white-space: pre-wrap;
  display: flex;
  flex-direction: column;

  background: ${({ $isUser }) =>
    $isUser ? 'var(--color-chat-user)' : 'transparent'};
  color: ${({ $isUser }) =>
    $isUser ? 'var(--color-text)' : 'var(--color-chat-assistant-text)'};
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  text-align: ${({ $isUser }) => ($isUser ? 'right' : 'left')};
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
  transition: opacity ${theme.transitions.fast};

  &:hover {
    opacity: 1;
  }
`;

const CrisisAlertBox = styled.div`
  background: #FFF3CD;
  border: 1px solid #FFD866;
  border-radius: 12px;
  padding: 20px;
  color: #664D03;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  line-height: 1.6;
  align-self: stretch;

  a {
    color: #834CC9;
    font-weight: 600;
    word-break: break-all;
  }
`;

const CrisisAlertNotice = styled.div`
  margin-top: 12px;
  font-size: 11px;
  line-height: 1.4;
  opacity: 0.65;
`;

const RedirectAgentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 20px;
  background: var(--color-primary);
  border: none;
  border-radius: 14px;
  color: #FFFFFF;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  width: 100%;
  justify-content: center;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 16px;
  background: rgba(131, 76, 201, 0.15);
  border: 1px solid rgba(131, 76, 201, 0.3);
  border-radius: 20px;
  color: #b794e0;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: rgba(131, 76, 201, 0.25);
    border-color: rgba(131, 76, 201, 0.5);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ActionButton = styled.button<{ $isActive?: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  color: ${({ $isActive }) => ($isActive ? '#FF8BD1' : '#666666')};

  &:hover {
    color: #FF8BD1;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MarkdownContent = styled.div`
  /* Reset white-space so markdown paragraphs collapse properly */
  white-space: normal;

  p {
    margin: 0 0 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 700;
    color: var(--color-text);
  }

  a {
    color: #FF8BD1;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ol, ul {
    margin: 4px 0;
    padding-left: 22px;
  }

  li {
    margin: 2px 0;
    padding: 0;
    line-height: 1.5;

    p {
      margin: 0;

      strong:only-child {
        display: inline-block;
      }
    }

    > p + p {
      margin-top: 2px;
    }
  }

  ol li::marker {
    font-weight: 600;
  }

  li > ol,
  li > ul {
    margin: 2px 0;
  }

  /* Heading spacing */
  h1, h2, h3, h4, h5, h6 {
    margin: 8px 0 4px;

    &:first-child {
      margin-top: 0;
    }
  }

  code {
    background: var(--color-border-subtle);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', Consolas, monospace;
    font-size: 12px;
  }

  pre {
    background: var(--color-border-subtle);
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 6px 0;

    code {
      background: none;
      padding: 0;
    }
  }
`;

const InlineLinkChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(131, 76, 201, 0.15);
  border: 1px solid rgba(131, 76, 201, 0.3);
  border-radius: 10px;
  color: ${theme.colors.secondary};
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all ${theme.transitions.fast};
  margin: 4px 0;
  cursor: pointer;

  &:hover {
    background: rgba(131, 76, 201, 0.25);
    border-color: ${theme.colors.secondary};
  }

  svg {
    flex-shrink: 0;
    color: ${theme.colors.secondary};
  }
`;

const StudiosContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: ${theme.spacing.lg};
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 0 ${theme.spacing.sm} 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  width: 100%;

  /* Ensure cards scroll properly */
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) var(--color-bg-elevated);

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--color-bg-elevated);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 3px;

    &:hover {
      background: var(--color-border);
    }
  }

  > * {
    scroll-snap-align: start;
    flex-shrink: 0;
  }

  /* First card left padding */
  > *:first-of-type {
    margin-left: 0;
  }

  /* Last card right padding */
  > *:last-of-type {
    margin-right: 0;
  }
`;

const IntroText = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.sm};
  color: var(--color-chat-assistant-text);
  margin-bottom: ${theme.spacing.sm};
`;


const MessageImageThumbnail = styled.div`
  margin-top: ${theme.spacing.sm};
  border-radius: 8px;
  overflow: hidden;
  max-width: 120px;
  cursor: pointer;
  transition: transform ${theme.transitions.fast};

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
    border: 1px solid var(--color-border-subtle);
  }
`;

const HIDDEN_PREFIXES = ['[RECOMMENDED CONTENT]', '[WELLNESSPASS RECOMMENDATIONS]'];

const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({
  message,
  onLike,
  onDislike,
  showActions = true,
  onStudioClick,
  onEventClick,
  onInternalLinkClick,
  onRedirectToAgent,
}) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);

  const handleLike = () => {
    setFeedback(feedback === 'like' ? null : 'like');
    onLike?.(message.dbId ?? message.id);
  };

  const handleDislike = () => {
    setFeedback(feedback === 'dislike' ? null : 'dislike');
    onDislike?.(message.dbId ?? message.id);
  };

  // Extract domain from URL or format deep link
  const extractDomain = (url: string): string => {
    // Handle meditopia:// deep links
    if (url.startsWith('meditopia://')) {
      const path = url.replace('meditopia://', '');
      // Capitalize first letter and make it more readable
      return path.split('/')[0].charAt(0).toUpperCase() + path.split('/')[0].slice(1);
    }

    // Handle HTTP(S) URLs
    try {
      const urlObj = new URL(url);

      // Friendly labels for known Meditopia service subdomains
      const serviceLabels: Record<string, string> = {
        'wellnesspass-app.meditopia.com': 'WellnessPass',
        'webapp.meditopia.com': '1-1 Sessions',
        'point-system.meditopia.com': 'Points & Rewards',
        'social.meditopia.com': 'Social',
      };

      const label = serviceLabels[urlObj.hostname];
      if (label) return label;

      // Special handling for meditopia.com URLs
      if (urlObj.hostname.includes('meditopia.com')) {
        const pathSegments = urlObj.pathname.split('/').filter(Boolean);
        // Skip language code (e.g., 'en', 'tr')
        const mainPath = pathSegments.length > 1 && pathSegments[0].length === 2
          ? pathSegments[1]
          : pathSegments[0] || 'Meditopia';
        return mainPath.charAt(0).toUpperCase() + mainPath.slice(1);
      }

      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Parse persisted content recommendations from DB history
  const savedRecommendations = useMemo(() => {
    const match = message.content.match(/__CONTENT_RECOMMENDATIONS_DATA__\n([\s\S]*?)\n__END_CONTENT_RECOMMENDATIONS_DATA__/);
    if (!match) return null;
    try {
      return JSON.parse(match[1]);
    } catch {
      return null;
    }
  }, [message.content]);

  // Detect agent redirect markers
  const hasRedirectMH = !message.isStreaming && message.content.includes('__REDIRECT_MENTAL_HEALTH__');
  const hasRedirectFAQ = !message.isStreaming && message.content.includes('__REDIRECT_FAQ__');

  // Parse studios data, live events data, and breathing exercises from message content
  const { intro, studios, liveEvents, breathingExercises, regularContent } = useMemo(() => {
    const studiosMatch = message.content.match(/__STUDIOS_DATA__\n([\s\S]*?)\n__END_STUDIOS_DATA__/);
    const liveEventsMatch = message.content.match(/__LIVE_EVENTS_DATA__\n([\s\S]*?)\n__END_LIVE_EVENTS_DATA__/);
    const breathingMatch = message.content.match(/__BREATHING_EXERCISES__\n?([\s\S]*?)\n?__END_BREATHING_EXERCISES__/);

    if (studiosMatch) {
      const studiosJson = studiosMatch[1];
      const introText = message.content.substring(0, studiosMatch.index).trim();

      try {
        const parsedStudios: Studio[] = JSON.parse(studiosJson);
        return {
          intro: introText,
          studios: parsedStudios,
          liveEvents: null,
          breathingExercises: null,
          regularContent: null,
        };
      } catch (error) {
        console.error('Error parsing studios data:', error);
      }
    }

    if (liveEventsMatch) {
      const liveEventsJson = liveEventsMatch[1];
      const introText = message.content.substring(0, liveEventsMatch.index).trim();

      try {
        const parsedEvents = JSON.parse(liveEventsJson);
        return {
          intro: introText,
          studios: null,
          liveEvents: parsedEvents,
          breathingExercises: null,
          regularContent: null,
        };
      } catch (error) {
        console.error('Error parsing live events data:', error);
      }
    }

    if (breathingMatch) {
      const breathingJson = breathingMatch[1].trim();
      const introText = message.content.substring(0, breathingMatch.index).trim();

      try {
        const parsedExercises: BreathingExercise[] = JSON.parse(breathingJson);
        const validIds = ['relax', 'focus', 'unwind', 'energize'];
        const filtered = parsedExercises.filter(e => validIds.includes(e.id));
        if (filtered.length > 0) {
          return {
            intro: introText,
            studios: null,
            liveEvents: null,
            breathingExercises: filtered,
            regularContent: null,
          };
        }
      } catch (error) {
        console.error('Error parsing breathing exercises data:', error);
      }
    }

    // Clean up internal markers that should never be shown to users
    let content = message.content
      .replace(/__TRIGGER_CONTENT_RECOMMENDATION__/g, '')
      .replace(/__END_CONVERSATION__/g, '')
      .replace(/__REDIRECT_MENTAL_HEALTH__/g, '')
      .replace(/__REDIRECT_FAQ__/g, '')
      .replace(/Here are some content recommendations suited to your needs\./gi, '')
      // Strip the standalone '@' end-of-conversation marker wherever it appears,
      // while preserving '@' inside email addresses (help@meditopia.com).
      .replace(/(?<!\w)@(?!\w)/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // During streaming, hide partial breathing marker to prevent flicker
    if (message.isStreaming && content.includes('__BREATHING_EXERCISES__') && !content.includes('__END_BREATHING_EXERCISES__')) {
      content = content.substring(0, content.indexOf('__BREATHING_EXERCISES__')).trim();
    }

    return {
      intro: null,
      studios: null,
      liveEvents: null,
      breathingExercises: null,
      regularContent: content,
    };
  }, [message.content, message.isStreaming]);

  // Process content to replace URLs and deep links with inline chips
  const processedContent = useMemo(() => {
    if (isUser || !regularContent) return regularContent || message.content;

    // Remove the trigger phrase from display
    let content = regularContent.replace(/Here are some content recommendations suited to your needs\./gi, '').trim();

    // Match both HTTP(S) URLs and meditopia:// deep links
    // Match URLs that are NOT part of markdown link syntax [text](url)
    const urlRegex = /(?<!\]\()(?<!\()(?:https?:\/\/|meditopia:\/\/)[\w\-._~:/?#[\]@!$&'()*+,;=]+/g;
    const parts: Array<{ type: 'text' | 'link'; content: string; url: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(content)) !== null) {
      const url = match[0];

      // Check if this URL is part of markdown link syntax by looking back
      const beforeUrl = content.substring(Math.max(0, match.index - 2), match.index);
      if (beforeUrl === '](' || beforeUrl.endsWith('](')) {
        continue; // Skip URLs that are part of markdown links
      }

      // Add text before the URL
      if (match.index > lastIndex) {
        const textPart = content.slice(lastIndex, match.index);
        parts.push({
          type: 'text',
          content: textPart,
          url: '',
        });
      }

      // Add the URL as a link
      parts.push({
        type: 'link',
        content: url,
        url: url,
      });

      lastIndex = match.index + url.length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex),
        url: '',
      });
    }

    return parts.length > 0 ? parts : null;
  }, [regularContent, isUser]);

  // Hide internal/system payload messages entirely. These checks live AFTER
  // every hook call: message.content changes during streaming, and an early
  // return above the hooks would change the hook count between renders.
  if (HIDDEN_PREFIXES.some((p) => message.content.trimStart().startsWith(p))) {
    return null;
  }
  if (isUser && message.content.trim() === '__END_CONVERSATION__') {
    return null;
  }

  // Crisis alert — render as yellow warning box.
  // Render URLs as proper React anchors so the host WebView can intercept them
  // via onInternalLinkClick (target="_blank" silently fails inside iOS WKWebView
  // when the host doesn't implement WKUIDelegate).
  if (message.crisisAlert) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const segments: Array<{ type: 'text' | 'link'; value: string }> = [];
    let cursor = 0;
    let m: RegExpExecArray | null;
    while ((m = urlRegex.exec(message.content)) !== null) {
      if (m.index > cursor) segments.push({ type: 'text', value: message.content.slice(cursor, m.index) });
      segments.push({ type: 'link', value: m[0] });
      cursor = m.index + m[0].length;
    }
    if (cursor < message.content.length) segments.push({ type: 'text', value: message.content.slice(cursor) });

    return (
      <BubbleContainer $isUser={false} className="selectable">
        <CrisisAlertBox>
          {segments.map((seg, i) => seg.type === 'link' ? (
            <a
              key={i}
              href={seg.value}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (onInternalLinkClick && onInternalLinkClick(seg.value)) {
                  e.preventDefault();
                }
              }}
            >
              {seg.value}
            </a>
          ) : (
            <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{seg.value}</span>
          ))}
          {message.crisisNotice && (
            <CrisisAlertNotice>{message.crisisNotice}</CrisisAlertNotice>
          )}
        </CrisisAlertBox>
      </BubbleContainer>
    );
  }

  return (
    <BubbleContainer $isUser={isUser} className="selectable">
      {/* Render persisted recommendations from DB history */}
      {savedRecommendations && savedRecommendations.length > 0 && (
        <ContentRecommendations recommendations={savedRecommendations} />
      )}

      {/* Render studios if present */}
      {!savedRecommendations && studios && (
        <>
          {intro && <IntroText>{intro}</IntroText>}
          <StudiosContainer>
            {studios.map((studio) => (
              <StudioCard key={studio.studioID} studio={studio} onStudioClick={onStudioClick} />
            ))}
          </StudiosContainer>
        </>
      )}

      {/* Render live events if present */}
      {liveEvents && (
        <>
          {intro && <IntroText>{intro}</IntroText>}
          <LiveEvents events={liveEvents} onEventClick={onEventClick} />
        </>
      )}

      {/* Render breathing exercises if present */}
      {breathingExercises && (
        <>
          {intro && <IntroText>{intro}</IntroText>}
          <StudiosContainer>
            {breathingExercises.map((exercise) => (
              <BreathingExerciseCard key={exercise.id} exercise={exercise} />
            ))}
          </StudiosContainer>
        </>
      )}

      {/* Render regular message content */}
      {!savedRecommendations && !studios && !liveEvents && !breathingExercises && (
        <Bubble $isUser={isUser}>
          {isUser ? (
            // User messages - plain text and image if present
            <>
              {message.content}
              {message.imageUrl && (
                <MessageImageThumbnail>
                  <img src={message.imageUrl} alt="User uploaded" />
                </MessageImageThumbnail>
              )}
            </>
          ) : (
            // Assistant messages - render text with inline link chips
            <MarkdownContent>
              {(() => {
                // Custom anchor renderer — lets us intercept markdown-formatted
                // therapy links so they open inside the iframe sheet instead of
                // a new tab (token appended at click time, never stored in DB).
                const markdownComponents = {
                  a: ({ href, children, ...rest }: any) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (href && onInternalLinkClick && onInternalLinkClick(href)) {
                          e.preventDefault();
                        }
                      }}
                      {...rest}
                    >
                      {children}
                    </a>
                  ),
                };
                return Array.isArray(processedContent) ? (
                processedContent.map((part, index) => {
                  if (part.type === 'text') {
                    return (
                      <ReactMarkdown key={index} remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {part.content}
                      </ReactMarkdown>
                    );
                  } else {
                    return (
                      <InlineLinkChip
                        key={index}
                        href={part.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (onInternalLinkClick && onInternalLinkClick(part.url)) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <LinkIcon size={12} weight="bold" />
                        {extractDomain(part.url)}
                      </InlineLinkChip>
                    );
                  }
                })
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {processedContent || message.content}
                </ReactMarkdown>
              );
              })()}
            </MarkdownContent>
          )}
        </Bubble>
      )}

      {/* Render inline recommendations attached to this message */}
      {message.recommendations && message.recommendations.length > 0 && (
        <ContentRecommendations recommendations={message.recommendations} />
      )}

      {/* Share button for conversation summary */}
      {message.conversationSummary && !message.isStreaming && (
        <ShareButton onClick={() => {
          const text = message.conversationSummary!;
          if (navigator.share) {
            navigator.share({ text }).catch(() => {});
          } else {
            navigator.clipboard.writeText(text).then(() => {
              alert('Copied to clipboard');
            }).catch(() => {});
          }
        }}>
          <ShareIcon size={16} weight="regular" />
          Share Summary
        </ShareButton>
      )}

      {/* Mental health redirect button */}
      {hasRedirectMH && onRedirectToAgent && (
        <RedirectAgentButton onClick={() => onRedirectToAgent('mental-health')}>
          <Heart size={18} weight="fill" />
          Mental Sağlık Asistanına Git
        </RedirectAgentButton>
      )}

      {/* FAQ redirect button */}
      {hasRedirectFAQ && onRedirectToAgent && (
        <RedirectAgentButton onClick={() => onRedirectToAgent('faq')}>
          <Heart size={18} weight="fill" />
          FAQ Asistanına Git
        </RedirectAgentButton>
      )}

      {isAssistant && showActions && !message.isStreaming && (
        <ActionsContainer>
          <ActionButton
            onClick={handleLike}
            aria-label="Like message"
            $isActive={feedback === 'like'}
          >
            <ThumbsUp size={20} weight="regular" />
          </ActionButton>
          <ActionButton
            onClick={handleDislike}
            aria-label="Dislike message"
            $isActive={feedback === 'dislike'}
          >
            <ThumbsDown size={20} weight="regular" />
          </ActionButton>
        </ActionsContainer>
      )}
    </BubbleContainer>
  );
};

// Memoized: during streaming only the actively streaming message gets a new
// object identity from useChat, so every other bubble skips re-rendering on
// each token — provided parents pass stable (useCallback) handlers.
export const MessageBubble = memo(MessageBubbleComponent);

